import {trans} from '@frontend/helpers'
import type {
    AdPreviewDataForGrid,
    AdPreviewDataForList,
    AdsListPresentation,
    FilteredItemsListPageHeaderInfo,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {
    type FilteredItemsResponseData,
    PublicAdsApi,
} from '@frontend/modules/public/ads/PublicAdsApi'
import type {ExecutorPreviewData} from '@frontend/modules/public/services/ServicesModuleTypes'
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {scrollToTop} from 'swayok-react-mdb-ui-kit/helpers/scrollToTop'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'
import type {AnalyticsItemsListId} from '../../../../types/AnalyticsService'

type AcceptedTypes = AdPreviewDataForGrid | AdPreviewDataForList | ExecutorPreviewData

export interface UseFilteredItemsLoaderHookOptions<T extends AcceptedTypes> {
    type: 'ads' | 'services'
    listIdForAnalytics?: AnalyticsItemsListId
    categoryId?: number | null
    filters?: FilteredItemsPageUrlQueryParams
    pageLimit?: number
    adsPresentation?: AdsListPresentation
    silenceErrors?: boolean
    onSuccess?: (data: FilteredItemsResponseData<T>, action: FilteredItemsLoaderAction) => void
    setHeaderInfo?: (header: FilteredItemsListPageHeaderInfo) => void
    onError?: (error: ApiError) => void
}

export interface UseFilteredItemsLoaderHookReturn<T extends AcceptedTypes> {
    loadItems: (
        action: FilteredItemsLoaderAction,
        silent?: boolean,
        infiniteScroll?: boolean
    ) => void
    items: T[]
    totalItemsCount: number | null
    isLoading: FilteredItemsLoaderAction | null
    loadingError: boolean
    isLoadedOnce: boolean
    isAllItemsLoaded: boolean
    onItemDataChange: (updatedItem: {id: number;} & Partial<T>) => void
}

export type FilteredItemsLoaderAction = 'reload' | 'next' | 'filter'

// Загрузка списка объявлений или услуг.
export function useFilteredItemsLoader<T extends AcceptedTypes>(
    options: UseFilteredItemsLoaderHookOptions<T>
): UseFilteredItemsLoaderHookReturn<T> {

    const {
        type,
        listIdForAnalytics,
        categoryId = null,
        filters = {},
        adsPresentation = 'grid',
        pageLimit = type === 'ads'
            ? (adsPresentation === 'list' ? 10 : 20)
            : 10,
        silenceErrors,
        onSuccess,
        onError,
        setHeaderInfo,
    } = options

    // Загруженные данные.
    const [
        items,
        setItems,
    ] = useState<T[]>([])
    const [
        totalItemsCount,
        setTotalItemsCount,
    ] = useState<number | null>(null)

    // Ошибки и состояния активности интерактивных элементов.
    const [
        isLoading,
        setIsLoading,
    ] = useState<FilteredItemsLoaderAction | null>('reload')
    const [
        isAllItemsLoaded,
        setIsAllItemsLoaded,
    ] = useState<boolean>(false)
    const [
        loadingError,
        setLoadingError,
    ] = useState<boolean>(false)

    // Функция отмены загрузки данных.
    const abortControllerRef = useRef<AbortController | null>(null)

    // Если при демонтаже компонента идёт выполнение запроса, нужно его отменить.
    useEffect(
        () => () => abortControllerRef.current?.abort(),
        []
    )

    // Обновить данные объявления.
    const onItemDataChange = useCallback(
        (
            updatedItem: {id: number;} & Partial<T>
        ) => {
            setItems(items => {
                const index = items.findIndex(
                    item => item.id === updatedItem.id
                )
                if (index < 0) {
                    return items
                }
                const updates = items.slice()
                updates[index] = {...updates[index], ...updatedItem}
                return updates
            })
        },
        []
    )

    // Загрузка объявлений (страницами).
    const loadItems = useEventCallback((
        action: FilteredItemsLoaderAction,
        silent: boolean = false,
        infiniteScroll: boolean = false
    ) => {
        // Если в данный момент идёт выполнение другого запроса, нужно его отменить.
        abortControllerRef.current?.abort()
        const newAbortController: AbortController = new AbortController()
        abortControllerRef.current = newAbortController
        if (!silent) {
            setIsLoading(action)
        }
        setLoadingError(false)
        if (action !== 'next') {
            scrollToTop()
        }

        const offset = action === 'next' ? items.length : 0
        PublicAdsApi.getFilteredItems<T>(
            categoryId ?? null,
            type === 'services' ? 'services' : 'items',
            filters,
            pageLimit,
            offset,
            adsPresentation,
            newAbortController
        )
            .then(data => {
                const allItemsLoaded: boolean = !data.items || data.items.length < pageLimit
                setItems(
                    items => (
                        action === 'next'
                            ? items.concat(data.items ?? [])
                            : (data.items ?? [])
                    )
                )
                setTotalItemsCount(data.itemsCount ?? 0)
                setIsAllItemsLoaded(allItemsLoaded)
                setIsLoading(null)

                abortControllerRef.current = null
                if (action === 'next' && allItemsLoaded && !infiniteScroll) {
                    ToastService.info(
                        type === 'services'
                            ? trans().services.messages.all_services_loaded
                            : trans().ads.messages.all_ads_loaded
                    )
                }

                onSuccess?.(data, action)
                setHeaderInfo?.({
                    isLoadedOnce: true,
                    header: data.header ?? null,
                    categoriesPath: data.path ?? [],
                    totalItemsCount: data.itemsCount ?? 0,
                })

                if (type === 'ads') {
                    AnalyticsEventReporter.adsListLoaded(
                        categoryId,
                        filters,
                        offset,
                        data.items as AdPreviewDataForGrid[] | AdPreviewDataForList[],
                        listIdForAnalytics
                    )
                }
            })
            .catch((error: ApiError) => {
                if (error.errorType === 'abort') {
                    // Запрос отменён - всё нормально, реагировать на это не нужно.
                    return
                }
                setIsLoading(null)
                abortControllerRef.current = null
                if (items.length === 0) {
                    setLoadingError(true)
                }
                if (action !== 'next') {
                    setItems([])
                    setTotalItemsCount(0)
                }
                onError?.(error)
                if (!onError && !silenceErrors) {
                    handleErrorResponse(error)
                }
            })
    })

    return {
        loadItems,
        items,
        totalItemsCount,
        isLoading,
        loadingError,
        isLoadedOnce: totalItemsCount !== null,
        isAllItemsLoaded,
        onItemDataChange,
    }
}
